import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { throwError } from 'rxjs';
import type { Producto, TopMarca } from '../types/product.types';
import { ApiMembresiaResponse } from '../types/API.types';
import { environment } from '../environments/environment';
import { CategoriesService } from './categories.service';
import { VehicleService } from './vehicle.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private apiUrl = `${environment.apiUrl}/${environment.apiVersion}`;
  private http = inject(HttpClient);
  private categoriesService = inject(CategoriesService);
  private vehicleService = inject(VehicleService);
  /**
   * Obtiene un producto por su slug
   * @param slug - Slug del producto
   * @returns Observable con el producto encontrado
   */
  getProductBySlug(slug: string) {
    // Por ahora retornamos null, esto se implementará cuando tengamos el endpoint específico
    return this.getProductBySku(slug);
  }

  /**
   * Obtiene un producto por su SKU
   * @param sku - SKU del producto
   * @returns Observable con el producto encontrado
   */
  getProductBySku(sku: string) {
    const url = `${this.apiUrl}${environment.endpoints.products.getBySku}/${sku}`;

    return this.http.get<ApiMembresiaResponse<Producto>>(url).pipe(
      map((response) => {
        return response?.data;
      }),
      catchError((error) => {
        console.error('❌ Error obteniendo producto por SKU:', error);
        return throwError(() => error);
      })
    );
  }

  getProductsBySlugs(slugs: string[]) {
    return this.getProductsBySkus(slugs);
  }

  getProductsBySkus(skus: string[]) {
    const firstSku = skus[0];
    let params = `?skus=${firstSku}`;
    params = skus.reduce((acc, sku) => `${acc}&skus=${sku}`, params);
    const url = `${this.apiUrl}${environment.endpoints.products.getBySkus}${params}`;

    return this.http.get<ApiMembresiaResponse<Producto[]>>(url).pipe(
      map((response) => {
        return response?.data || [];
      }),
      catchError((error) => {
        console.error('❌ Error obteniendo productos por SKUs:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Obtiene todos los productos con filtros opcionales
   * @param idGrupo - ID del grupo para filtrar productos
   * @returns Observable con la lista de productos
   */
  getAllProducts({
    idGrupo,
    marca,
    limit,
  }: {
    idGrupo?: number;
    marca?: string;
    limit?: number;
  }) {
    const url = `${this.apiUrl}${environment.endpoints.products.getAll}`;
    const vehiculoPorDefault = this.vehicleService.getDefaultVehicle();

    // Construir parámetros de consulta
    let httpParams = new HttpParams();
    if (idGrupo) {
      httpParams = httpParams.set('id_grupo', idGrupo.toString());
    }

    if (marca) {
      httpParams = httpParams.set('marca', marca);
    }

    if (limit) {
      httpParams = httpParams.set('limit', limit.toString());
    }

    if (vehiculoPorDefault?.idSubmodeloMotor) {
      httpParams = httpParams.set(
        'id_submodelo_motor',
        vehiculoPorDefault.idSubmodeloMotor.toString()
      );
    }

    return this.http
      .get<ApiMembresiaResponse<Producto[]>>(url, { params: httpParams })
      .pipe(
        map((response) => {
          return response?.data || [];
        }),
        catchError((error) => {
          console.error('❌ Error obteniendo productos:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Obtiene productos por categoría (usando ID del grupo)
   * @param categorySlug - Slug de la categoría
   * @returns Observable con la lista de productos de la categoría
   */
  getProductsByCategory(categorySlug: string) {
    const category = this.categoriesService.findCategoryBySlug(categorySlug);

    if (!category) {
      return of([]);
    }

    // Convertir el ID de string a number
    const idGrupo = category.id;

    if (isNaN(idGrupo)) {
      return of([]);
    }

    return this.getAllProducts({ idGrupo });
  }

  /**
   * Obtiene productos por marca (filtrado local)
   * @param marca - Marca del producto
   * @param products - Lista de productos donde buscar
   * @returns Array de productos de la marca especificada
   */
  getProductsByBrand(marca: string, products: Producto[]): Producto[] {
    if (!products || !marca) return [];

    return products.filter(
      (product) => product.marca.toLowerCase() === marca.toLowerCase()
    );
  }

  /**
   * Busca productos por término de búsqueda
   * @param searchTerm - Término de búsqueda
   * @param products - Lista de productos donde buscar
   * @returns Array de productos que coinciden con la búsqueda
   */
  searchProducts(products: Producto[], searchTerm: string): Producto[] {
    if (!searchTerm || !products) {
      return products || [];
    }

    const term = searchTerm.toLowerCase().trim();

    return products.filter(
      (product) =>
        product.descripcion.toLowerCase().includes(term) ||
        product.marca.toLowerCase().includes(term) ||
        product.part_number.toLowerCase().includes(term) ||
        (product.sku && product.sku.toLowerCase().includes(term)) ||
        product.categoria.toLowerCase().includes(term) ||
        product.subcategoria.toLowerCase().includes(term) ||
        // Buscar también en atributos
        (product.atributos &&
          product.atributos.some(
            (attr) =>
              attr.label.toLowerCase().includes(term) ||
              String(attr.value).toLowerCase().includes(term)
          ))
    );
  }

  /**
   * Obtiene las marcas más vendidas
   * @returns Observable con la lista de marcas más vendidas
   */
  getTopMarcas() {
    const url = `${this.apiUrl}${environment.endpoints.products.getTopMarcas}`;
    return this.http.get<ApiMembresiaResponse<TopMarca[]>>(url).pipe(
      map((response) => {
        return response?.data || [];
      }),
      catchError((error) => {
        console.error('❌ Error obteniendo marcas más vendidas:', error);
        return throwError(() => error);
      })
    );
  }

  verifyProductFitsVehicle(
    oem: string,
    idSubmodeloMotor: number,
    idGrupo: number
  ) {
    const url = `${this.apiUrl}${environment.endpoints.products.verifyProductFitsVehicle}/${oem}/${idSubmodeloMotor}/${idGrupo}`;

    return this.http.get<ApiMembresiaResponse<boolean>>(url).pipe(
      map((response) => {
        return response?.data || false;
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }
}
